import type { Repository } from "../data/repository";

export type LlmMode = "proxy" | "byok";

export interface LlmSettings {
  // proxy: calls route through STRAD's stateless proxy (default on the hosted
  // subdomain). byok: the user's own key is sent direct to the model, never to
  // STRAD. The key lives only in IndexedDB.
  mode: LlmMode;
  proxyUrl: string;
  apiKey: string;
  model: string;
}

// The deployed STRAD proxy Worker URL. Baked in so the hosted app defaults to
// the proxy with no per-user setup. VITE_PROXY_URL (Pages build env) overrides
// it; set this constant once the Worker is deployed if you would rather not use
// a build env var. Empty keeps the graceful "model unavailable" path in dev.
const DEFAULT_PROXY_URL = "https://trust-lens-proxy.piscaer.workers.dev";

export const DEFAULT_SETTINGS: LlmSettings = {
  mode: "proxy",
  proxyUrl: import.meta.env.VITE_PROXY_URL ?? DEFAULT_PROXY_URL,
  apiKey: "",
  model: "claude-sonnet-4-6",
};

const CONFIG_KEY = "llm";

export async function loadSettings(repo: Repository): Promise<LlmSettings> {
  const raw = await repo.getConfig(CONFIG_KEY);
  if (!raw) return { ...DEFAULT_SETTINGS };
  try {
    const merged = { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<LlmSettings>) };
    // A browser that saved settings before the proxy URL was baked in stored an
    // empty proxyUrl, which would otherwise shadow the new default. Fall back to
    // the baked default when the saved one is blank, so the proxy works without
    // the user re-entering it. An explicit non-empty proxyUrl is left untouched.
    if (!merged.proxyUrl.trim()) merged.proxyUrl = DEFAULT_SETTINGS.proxyUrl;
    return merged;
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export async function saveSettings(
  repo: Repository,
  settings: LlmSettings,
): Promise<void> {
  await repo.setConfig(CONFIG_KEY, JSON.stringify(settings));
}

export function isConfigured(s: LlmSettings): boolean {
  return s.mode === "proxy" ? s.proxyUrl.trim() !== "" : s.apiKey.trim() !== "";
}
